import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const privacySections = [
  {
    title: "1. Scope",
    paragraphs: [
      "This Privacy Policy applies to Josh features that help users manage tasks, connected inboxes, notifications, shopping, travel, reminders, and bill or expense tracking. It is designed around the data flows currently implemented in Josh’s codebase and is intended to align with Apple platform disclosure requirements and major privacy frameworks, including U.S. state privacy laws, the GDPR and UK GDPR, Canada’s PIPEDA, and Australian and New Zealand privacy principles.",
    ],
  },
  {
    title: "2. Information Josh collects",
    bullets: [
      "Account information such as your full name, email address, authentication identifiers, plan tier, and account creation timestamps.",
      "Profile and preference information such as onboarding state, environment preferences, saved channels, vendor records, reminders, and notification preferences.",
      "Command, conversation, workflow, and approval data so Josh can understand requests, continue tasks, keep activity history, and present results back to you.",
      "Connected inbox information when you choose to link Google or Microsoft email, including the email address on the connected account, OAuth tokens, selected scopes, message metadata, snippets, inferred billing categories, expense records, recurring purchase patterns, and monthly summary data.",
      "Payment and billing setup information when you choose to connect payment methods or utility billers, including masked account identifiers, provider names, billing email addresses, spending limits, Stripe-linked customer or payment method references, and utility payment records.",
      "Travel and commerce data when you ask Josh to search or prepare travel or shopping actions, including destinations, dates, occupancy details, booking previews, product selections, and handoff metadata.",
      "Connected messaging information if you connect WhatsApp or Telegram, including handles, verification events, and outbound message delivery metadata.",
      "Technical and usage information such as device session data, app interactions, request timestamps, and error or audit logs needed to secure the service and troubleshoot issues.",
    ],
  },
  {
    title: "3. Information Josh does not intentionally collect for advertising",
    paragraphs: [
      "Josh is not designed as an ad-tech product. Based on the current code, Josh does not use third-party advertising SDKs for cross-context behavioral advertising and does not sell personal information for money. Josh also does not request access to your full payment card number for ordinary Stripe setup flows, because payment credentials are handled through Stripe-hosted setup and tokenization flows.",
    ],
  },
  {
    title: "4. How Josh uses personal information",
    bullets: [
      "To create and secure your account, authenticate you, and maintain your session.",
      "To operate Josh’s assistant features, including understanding requests, asking clarifying questions, routing actions to the correct tools, and returning summaries, handoffs, or workflow results.",
      "To scan connected inboxes for bills, payments, subscriptions, renewals, receipts, recurring purchases, and expense events when you choose to connect your email.",
      "To build monthly expense summaries, recurring purchase reminders, and notifications based on the email and transaction signals Josh identifies for you.",
      "To process travel, shopping, routing, reminder, messaging, and utility workflows you ask Josh to perform.",
      "To prevent fraud, abuse, unauthorized access, and service misuse; to monitor performance; and to keep audit trails for compliance and debugging.",
      "To comply with legal obligations, enforce the Terms, and respond to lawful requests.",
    ],
  },
  {
    title: "5. Legal bases for processing",
    paragraphs: [
      "If you are in the EEA, UK, or another jurisdiction that requires a legal basis for processing, Josh generally processes your personal information on one or more of the following grounds:",
    ],
    bullets: [
      "Contract: to provide the service you asked for, such as connecting an inbox, preparing a booking, or sending a reminder.",
      "Consent: when you choose to connect external accounts, authorize a provider, or enable sensitive permissions.",
      "Legitimate interests: to secure the service, improve reliability, investigate abuse, and maintain operational records, provided those interests are not overridden by your rights.",
      "Legal obligation: when Josh must retain, disclose, or process information to comply with applicable law.",
    ],
  },
  {
    title: "6. External services and categories of sharing",
    paragraphs: [
      "Josh shares information with external services only when needed to run the service, when you choose to connect an integration, or when you instruct Josh to perform a partner action. Depending on the feature you use, Josh may disclose relevant data to the following categories of recipients:",
    ],
    bullets: [
      "Infrastructure and identity providers, including Firebase Authentication, Firestore, and Google Cloud hosting or secret management infrastructure.",
      "Email providers and identity providers, including Google and Microsoft, when you connect Gmail or Outlook.",
      "Payment processors such as Stripe, when you add or authorize payment methods or payment-related workflows.",
      "Travel and accommodation partners, including Booking.com Demand API, Amadeus, and travel search partners configured in Josh.",
      "Retail and location partners, including Amazon-related OpenWeb services, Best Buy, Waze, Travel Advisor via RapidAPI, or similar providers Josh uses to answer your shopping or directions requests.",
      "Messaging channels such as WhatsApp/Meta or Telegram, but only if you connect those services or ask Josh to message through them.",
      "AI and model providers used for conversational reasoning or fallback processing when enabled in Josh’s environment.",
      "Affiliate or commerce-link providers when Josh generates partner handoff or tracked commerce links.",
      "Partner apps you explicitly open through Josh, such as ride-hailing, travel, or merchant apps. Once you leave Josh, the partner’s own privacy policy applies.",
    ],
  },
  {
    title: "7. How much data is shared",
    paragraphs: [
      "Josh is designed to share only the data reasonably needed for the requested feature. For example, a travel booking flow may require traveler names, dates, and booking selections; a Gmail connection requires OAuth authorization and email message access within the scopes you approve; and a shopping handoff may share only the selected product or destination URL. Josh may also store internal metadata showing that a handoff happened, such as workflow IDs, timestamps, or provider response summaries.",
    ],
  },
  {
    title: "8. Retention",
    paragraphs: [
      "Josh keeps data for different periods depending on the feature and legal need. The code currently applies automated retention targets such as about 90 days for notifications and certain email outbound records, 180 days for activity logs, 30 days for some execution and booking job records, and approximately 365 days for travel bookings and utility payment records. Some approval and audit records may be kept longer where needed for security, fraud prevention, compliance, or dispute handling.",
      "Connected account tokens, inbox-derived summaries, and profile records may be retained until you disconnect the service, request deletion, or Josh no longer needs them to provide the feature, subject to legal obligations and secure backup or log retention practices.",
    ],
  },
  {
    title: "9. Your privacy rights",
    paragraphs: [
      "Depending on where you live, you may have rights to access, correct, export, delete, restrict, or object to certain processing of your personal information, and to withdraw consent where processing is based on consent.",
    ],
    bullets: [
      "Access and export: Josh’s backend includes a compliance export flow for user-scoped data.",
      "Deletion: Josh supports account or data deletion request handling and retention cleanup workflows.",
      "Correction: you can update profile and preference information in the app, and you can reconnect or disconnect supported providers.",
      "Opt-out and objection: you can mute recurring notifications, disconnect integrations, and stop using optional connected services.",
      "California rights: Josh does not state that it sells personal information, and Josh does not provide different pricing or quality based on the exercise of privacy rights, except where a feature logically requires data to function.",
    ],
  },
  {
    title: "10. Children’s privacy",
    paragraphs: [
      "Josh is not directed to children under 13, and where local law requires a higher age threshold for digital consent, Josh is not intended for users below that threshold without valid parental or guardian authorization. If you believe a child has provided personal information improperly, contact dpenhaaaron@gmail.com.",
    ],
  },
  {
    title: "11. Security",
    paragraphs: [
      "Josh uses authentication controls, server-side access controls, token encryption for connected inbox refresh tokens, provider-scoped credentials, and audit logging to protect personal information. No service can promise absolute security, and you are responsible for keeping your own devices, credentials, and connected third-party accounts secure.",
    ],
  },
  {
    title: "12. International transfers",
    paragraphs: [
      "Josh may process or store information in countries other than your own, including where Josh’s cloud providers or connected partners operate. Where required by law, Josh intends to rely on appropriate safeguards for cross-border transfers, such as contractual protections or other recognized transfer mechanisms.",
    ],
  },
  {
    title: "13. Apple platform disclosures",
    paragraphs: [
      "Josh is designed to request access to data and device capabilities only when relevant to features you choose to use, such as connected inboxes, notifications, and linked services. Josh is not intended to use your data for third-party advertising tracking. If Apple requires more detailed platform-specific disclosures, those disclosures may also appear in the App Store listing or platform privacy manifest alongside this policy.",
    ],
  },
  {
    title: "14. Changes to this policy",
    paragraphs: [
      "Josh may update this Privacy Policy to reflect service changes, new integrations, legal requirements, or operational improvements. If changes are material, Josh may provide additional notice in the app, by email, or through updated web posting before the changes take effect where required by law.",
    ],
  },
  {
    title: "15. Contact",
    paragraphs: [
      "Questions, privacy requests, or complaints about Josh can be sent to dpenhaaaron@gmail.com. If local law gives you the right to complain to a regulator, you may also contact your local data protection or privacy authority.",
    ],
  },
];

const JoshPrivacyPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
  };

  return (
    <div className="min-h-screen pt-20" data-testid="josh-privacy-page">
      <section className="py-16 md:py-24 bg-white">
        <div className="container-main">
          <motion.div {...fadeInUp} className="max-w-4xl mx-auto">
            <Link to="/joshai" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Hey Josh
            </Link>

            <p className="caption mb-4">Privacy Policy</p>
            <h1 className="heading-2 mb-6">Hey Josh</h1>
            <p className="body-text mb-10">
              Last updated March 16, 2026. This policy describes how Josh collects, uses,
              stores, and shares personal information when you use the Josh mobile app,
              web app, and related services.
            </p>

            <div className="space-y-6">
              {privacySections.map((section) => (
                <div key={section.title} className="feature-card">
                  <h2 className="heading-3 mb-4">{section.title}</h2>
                  {section.paragraphs?.map((paragraph) => (
                    <p key={paragraph} className="body-text mb-4 last:mb-0">
                      {paragraph}
                    </p>
                  ))}
                  {section.bullets && (
                    <ul className="space-y-3 text-base leading-relaxed text-zinc-600">
                      {section.bullets.map((bullet) => (
                        <li key={bullet} className="flex items-start gap-3">
                          <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 flex-shrink-0" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default JoshPrivacyPage;
